import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type LemurFinancingStatus =
  | 'processing'
  | 'ready'
  | 'finalizing'
  | 'completion_error'
  | 'approved'
  | 'completed'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'abandoned'
  | 'error'
  | 'provider_error'
  | 'polling_exhausted'
  | 'conflict';

export interface LemurConfigStatus {
  enabled: boolean;
  configured: boolean;
  usable: boolean;
  disclosureVersion: string;
  disclosureUrl: string;
  environment: 'sandbox' | 'live';
}

export interface LemurMoneyRange {
  minimum_cents: number | null;
  maximum_cents: number | null;
}

export interface LemurApplicationResponse {
  attemptId: string;
  attemptReference: string;
  applicationId: string | null;
  status: LemurFinancingStatus;
  providerStatus: string | null;
  approvedOffers: Array<{
    id: string;
    lender: {
      id: string;
      name: string;
      logo_url: string | null;
      disclaimer: string | null;
    };
    amount: LemurMoneyRange;
    apr: { minimum_percent: string | null; maximum_percent: string | null } | null;
    term: { minimum_months: number | null; maximum_months: number | null } | null;
    monthly_payment: LemurMoneyRange | null;
    continue_url: string;
  }> | null;
  completedLoan: {
    id: string;
    lender_id: string;
    lender_reference: string | null;
    status: 'completed';
    amount_cents: number;
  } | null;
  completionStatus: 'pending' | 'processing' | 'completed' | 'error' | null;
  warrantyId: string | null;
  paymentId: string | null;
  createdAt: string;
  updatedAt: string;
  terminalAt: string | null;
}

export interface LemurFinancingSubmitPayload {
  sessionId: string;
  attemptReference?: string;
  hash?: string;
  applicant: {
    dateOfBirth: string;
    governmentIdNumber: string;
    annualIncome: number;
    housingStatus: string;
    employmentStatus: string;
  };
  consent: {
    accepted: true;
    disclosureVersion: string;
  };
}

@Injectable()
export class LemurApplicationWebService {
  constructor(private http: HttpClient) {}

  private apiPrefix(staffView: boolean): string {
    return staffView ? '/v1/lemur' : '/api/lemur';
  }

  async getConfigStatus(): Promise<LemurConfigStatus> {
    return await firstValueFrom(this.http.get<LemurConfigStatus>('/api/lemur/config'));
  }

  async submitFromSession(payload: LemurFinancingSubmitPayload, staffView = false): Promise<LemurApplicationResponse> {
    return await firstValueFrom(
      this.http.post<LemurApplicationResponse>(`${this.apiPrefix(staffView)}/applications/from-session`, payload)
    );
  }

  async getStatusFromSession(
    sessionId: string,
    hash?: string | null,
    staffView = false
  ): Promise<LemurApplicationResponse> {
    return await firstValueFrom(
      this.http.get<LemurApplicationResponse>(
        `${this.apiPrefix(staffView)}/applications/from-session/${encodeURIComponent(sessionId)}`,
        { params: hash ? { hash } : {} }
      )
    );
  }
}
